const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_PREFIX_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/;
const MM_DD_YYYY_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const MM_DD_YYYY_DASH_PATTERN = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
const MILLIS_PER_DAY = 24 * 60 * 60 * 1000;

export const DEFAULT_BUSINESS_TIME_ZONE = 'America/New_York';

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function isValidDateParts(year: number, month: number, day: number): boolean {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  const candidate = new Date(Date.UTC(year, month - 1, day));
  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  );
}

function formatDateParts(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

function parseDateOnlyParts(value: string): { year: number; month: number; day: number } | null {
  const trimmed = value.trim();

  const dateOnlyMatch = trimmed.match(DATE_ONLY_PATTERN);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]);
    const day = Number(dateOnlyMatch[3]);
    return isValidDateParts(year, month, day) ? { year, month, day } : null;
  }

  const prefixMatch = trimmed.match(DATE_PREFIX_PATTERN);
  if (prefixMatch) {
    const year = Number(prefixMatch[1]);
    const month = Number(prefixMatch[2]);
    const day = Number(prefixMatch[3]);
    return isValidDateParts(year, month, day) ? { year, month, day } : null;
  }

  const mmddyyyyMatch = trimmed.match(MM_DD_YYYY_PATTERN);
  if (mmddyyyyMatch) {
    const month = Number(mmddyyyyMatch[1]);
    const day = Number(mmddyyyyMatch[2]);
    const year = Number(mmddyyyyMatch[3]);
    return isValidDateParts(year, month, day) ? { year, month, day } : null;
  }

  const mmddyyyyDashMatch = trimmed.match(MM_DD_YYYY_DASH_PATTERN);
  if (mmddyyyyDashMatch) {
    const month = Number(mmddyyyyDashMatch[1]);
    const day = Number(mmddyyyyDashMatch[2]);
    const year = Number(mmddyyyyDashMatch[3]);
    return isValidDateParts(year, month, day) ? { year, month, day } : null;
  }
  return null;
}

function toDateOnlyFromDate(value: Date): string | null {
  if (Number.isNaN(value.getTime())) return null;
  return formatDateParts(value.getFullYear(), value.getMonth() + 1, value.getDate());
}

function parseDateOnlyStrict(value: string): string | null {
  const parts = parseDateOnlyParts(value);
  if (!parts) return null;
  return formatDateParts(parts.year, parts.month, parts.day);
}

function dateOnlyToParts(value: string): { year: number; month: number; day: number } | null {
  const normalized = parseDateOnlyStrict(value);
  if (!normalized) return null;

  const match = normalized.match(DATE_ONLY_PATTERN);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  return { year, month, day };
}

function getFormatterForTimeZone(timeZone?: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function isValidIanaTimeZone(value: string): boolean {
  try {
    getFormatterForTimeZone(value);
    return true;
  } catch {
    return false;
  }
}

export function resolveBusinessTimeZone(
  ...candidates: Array<string | null | undefined>
): string {
  for (const candidate of candidates) {
    if (!candidate) continue;
    const trimmed = candidate.trim();
    if (!trimmed) continue;
    if (isValidIanaTimeZone(trimmed)) {
      return trimmed;
    }
  }

  return DEFAULT_BUSINESS_TIME_ZONE;
}

/**
 * Returns true when the input is strictly YYYY-MM-DD and represents a valid date.
 */
export function isDateOnly(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const match = value.trim().match(DATE_ONLY_PATTERN);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  return isValidDateParts(year, month, day);
}

/**
 * Normalizes supported date representations to canonical YYYY-MM-DD.
 */
export function normalizeToDateOnly(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) {
    return toDateOnlyFromDate(value);
  }

  if (typeof value !== 'string') return null;

  return parseDateOnlyStrict(value);
}

/**
 * Parses date-like values into a local-noon Date for timezone-safe UI display.
 */
export function parseDateOnlyForUI(value: unknown): Date | null {
  const normalized = normalizeToDateOnly(value);
  if (!normalized) return null;

  const parts = dateOnlyToParts(normalized);
  if (!parts) return null;

  const date = new Date(parts.year, parts.month - 1, parts.day, 12, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Alias for input controls that expect canonical YYYY-MM-DD.
 */
export function formatDateOnlyForInput(value: unknown): string | null {
  return normalizeToDateOnly(value);
}

export function compareDateOnly(a: string, b: string): -1 | 0 | 1 {
  const normalizedA = normalizeToDateOnly(a);
  const normalizedB = normalizeToDateOnly(b);

  if (!normalizedA || !normalizedB) return 0;
  if (normalizedA < normalizedB) return -1;
  if (normalizedA > normalizedB) return 1;
  return 0;
}

export function toEpochDay(dateOnly: string): number | null {
  const parts = dateOnlyToParts(dateOnly);
  if (!parts) return null;

  const millis = Date.UTC(parts.year, parts.month - 1, parts.day);
  return Math.floor(millis / MILLIS_PER_DAY);
}

export function addDaysDateOnly(dateOnly: string, deltaDays: number): string | null {
  const epochDay = toEpochDay(dateOnly);
  if (epochDay === null) return null;
  if (!Number.isFinite(deltaDays)) return null;

  const nextMillis = (epochDay + Math.trunc(deltaDays)) * MILLIS_PER_DAY;
  const next = new Date(nextMillis);

  return formatDateParts(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate());
}

export function getTodayDateOnly(timeZone?: string): string {
  const formatter = (() => {
    try {
      return getFormatterForTimeZone(timeZone);
    } catch {
      return getFormatterForTimeZone(resolveBusinessTimeZone());
    }
  })();

  const parts = formatter.formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;

  if (!year || !month || !day) {
    const fallback = new Date();
    return formatDateParts(fallback.getFullYear(), fallback.getMonth() + 1, fallback.getDate());
  }

  return `${year}-${month}-${day}`;
}
